import type {
  AppState, ChatEvent, ChatMessage, Equipment, HealthResponse, OnboardingStep,
  PantryItem, Preferences, Recipe, SavedRecipe, Settings,
} from '../../shared/types.ts'

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: init?.body ? { 'Content-Type': 'application/json', ...init?.headers } : init?.headers,
  })
  const text = await res.text()
  let data: unknown = null
  try { data = text ? JSON.parse(text) : null } catch { /* fall through to raw text */ }
  if (!res.ok) {
    const msg = (data as { error?: string })?.error ?? text.slice(0, 200) ?? res.statusText
    throw new Error(msg || `Request failed (${res.status})`)
  }
  return data as T
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) })

export const api = {
  health: () => request<HealthResponse>('/api/health'),
  state: () => request<AppState>('/api/state'),

  answerStep: (step: OnboardingStep, input: { text?: string; choices?: string[] }) =>
    request<{ summary: string; state: AppState }>('/api/onboarding/step', { method: 'POST', ...json({ step, ...input }) }),
  reopen: (step?: OnboardingStep) =>
    request<AppState>('/api/onboarding/reopen', { method: 'POST', ...json({ step }) }),

  clearChat: () => request<{ conversation: ChatMessage[] }>('/api/chat/clear', { method: 'POST' }),

  addEquipment: (items: Partial<Equipment>[]) =>
    request<{ equipment: Equipment[] }>('/api/equipment', { method: 'POST', ...json(items) }),
  updateEquipment: (id: string, patch: Partial<Equipment>) =>
    request<{ equipment: Equipment[] }>(`/api/equipment/${id}`, { method: 'PATCH', ...json(patch) }),
  removeEquipment: (id: string) =>
    request<{ equipment: Equipment[] }>(`/api/equipment/${id}`, { method: 'DELETE' }),

  addItems: (items: Partial<PantryItem>[]) =>
    request<{ pantry: PantryItem[] }>('/api/pantry', { method: 'POST', ...json(items) }),
  updateItem: (id: string, patch: Partial<PantryItem>) =>
    request<{ pantry: PantryItem[] }>(`/api/pantry/${id}`, { method: 'PATCH', ...json(patch) }),
  removeItem: (id: string) => request<{ pantry: PantryItem[] }>(`/api/pantry/${id}`, { method: 'DELETE' }),
  clearPantry: () => request<{ pantry: PantryItem[] }>('/api/pantry/clear', { method: 'POST' }),
  parseItems: (text: string) =>
    request<{ pantry: PantryItem[] }>('/api/pantry/parse', { method: 'POST', ...json({ text }) }),

  savePreferences: (p: Partial<Preferences>) => request<Preferences>('/api/preferences', { method: 'PUT', ...json(p) }),
  saveSettings: (s: Partial<Settings>) => request<Settings>('/api/settings', { method: 'PUT', ...json(s) }),

  saveRecipe: (recipe: Recipe, askedFor: string) =>
    request<{ saved: SavedRecipe[] }>('/api/saved', { method: 'POST', ...json({ recipe, askedFor }) }),
  unsaveRecipe: (id: string) => request<{ saved: SavedRecipe[] }>(`/api/saved/${id}`, { method: 'DELETE' }),
}

/**
 * POST /api/chat streams newline-delimited JSON. EventSource cannot POST, so the
 * body is read straight off the fetch reader and split on newlines.
 */
export async function streamChat(
  text: string,
  onEvent: (e: ChatEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const res = await fetch('/api/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
    signal,
  })

  if (!res.ok || !res.body) {
    const body = await res.text()
    let msg = `Request failed (${res.status})`
    try { msg = (JSON.parse(body) as { error?: string }).error ?? msg } catch { /* keep default */ }
    throw new Error(msg)
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''
  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += value
    let nl: number
    while ((nl = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, nl).trim()
      buffer = buffer.slice(nl + 1)
      if (line) {
        try { onEvent(JSON.parse(line) as ChatEvent) } catch { /* ignore a torn line */ }
      }
    }
  }
  const tail = buffer.trim()
  if (tail) {
    try { onEvent(JSON.parse(tail) as ChatEvent) } catch { /* ignore */ }
  }
}
